$border-width: 2px;
$width: 24px;
$width-small: 16px;
$height: 48px;
$heightWithoutBorder: $height - $border-width * 2;
$height-small: 36px;
$heightWithoutBorder-small: $height-small - $border-width * 2;
$line-height: 20px;
$line-height-small: 20px;
$corner-size: $width;
$corner-size-small: $width-small;
$margin-icon-text: 12px;

/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$button-border-size: 1px !default;

:host {
  box-sizing: border-box;
  display: inline-flex;
}

:host,
[type="button"],
[type="reset"],
[type="submit"] {
  @include button-reset-appearance;
}

:host([disabled]:not([disabled="false"])) {
  pointer-events: none;

  a,
  button {
    opacity: 0.6;
  }
}

:host(.#{$prefix}full-width) {
  display: block;
}

.#{$prefix}icon-container {
  display: grid;
  grid-template-areas: "content";
  justify-items: center;
  align-items: center;
  cursor: pointer;

  & > * {
    grid-area: content;
  }

  &.#{$prefix}icon-no-svg {
    background: v(primaryBackgroundColor);
    border: 1px solid v(primaryBorderColor);
    padding: 8px 13px;
    margin: 0;

    @if variable-exists(button-radius) {
      /* overwrite buttonRadius by parent component (default=$button-radius) */
      border-radius: var(--buttonRadius, #{$button-radius});
    }

    &:hover {
      background: v(primaryBackgroundHoverColor);
      border: 1px solid v(primaryBorderHoverColor);
    }
  }
}

a,
button {
  @include button-reset;
  @include eon-typography();

  font-weight: $font-weight-bold;
  line-height: $line-height;
  height: auto;
  min-height: $height;
  display: flex;
  flex-grow: 0;
  width: auto;
  text-align: center;

  .#{$prefix}content-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    position: relative;
  }

  /// hides the dashed inner focus line that FF draws. This is required.
  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    @include focusBorder(v(focusBorderColor));
  }

  /* Do not combine with pseudo-classes above!!!
     https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
  &:focus-within {
    @include focusBorder(v(focusBorderColor));
  }

  // for Edge V18
  &:hover {
    text-decoration: none;
  }

  svg {
    path.#{$prefix}outline {
      stroke-width: $border-width;
    }
  }

  .#{$prefix}content {
    display: flex;
    align-items: center;
    min-height: $heightWithoutBorder;
    font-weight: bold;
    position: relative;
    margin: 0px 23px 0px 23px;
    padding: 2px 1px;
    @include noneUserSelect();

    @if variable-exists(button-shadow) {
      box-shadow: #{$button-shadow};
    }

    &-no-svg {
      background: v(primaryBackgroundColor);
      border: 1px solid v(primaryBorderColor);
      padding: 1px 23px;
      margin: 0;

      @if variable-exists(button-radius) {
        border-radius: var(--buttonRadius, #{$button-radius});
      }

      &:hover {
        background: v(primaryBackgroundHoverColor);
        border: 1px solid v(primaryBorderHoverColor);
      }
    }

    span {
      position: relative;
      z-index: 1;
    }

    /* Dirty MS Edge 18 fix */
    @supports (-ms-ime-align: auto) {
      min-height: $height;
    }
  }

  .#{$prefix}content,
  .#{$prefix}top,
  .#{$prefix}bot,
  .#{$prefix}edge,
  .#{$prefix}icon-content,
  .#{$prefix}carrier,
  .#{$prefix}contentsvg,
  .#{$prefix}content svg,
  .#{$prefix}icon-container.#{$prefix}icon-no-svg {
    transition: background-color $transition-duration $transition-timing-function,
      color $transition-duration $transition-timing-function, border $transition-duration $transition-timing-function,
      opacity $transition-duration $transition-timing-function, fill $transition-duration $transition-timing-function,
      stroke $transition-duration $transition-timing-function;
  }

  .#{$prefix}contentsvg {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    object-position: center center;
    width: 100%;
    height: 100%;
  }

  .#{$prefix}corner,
  .#{$prefix}edge {
    position: absolute;
    width: $width;
    display: block;
    overflow: hidden;

    svg {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  .#{$prefix}left {
    left: 0;
  }

  .#{$prefix}right {
    right: 0;
  }

  .#{$prefix}bot {
    bottom: 0;
    border-top: 1px solid v(primaryBackgroundColor);
  }

  .#{$prefix}top {
    top: 0;
    border-bottom: 1px solid v(primaryBackgroundColor);
  }

  .#{$prefix}edge {
    top: $corner-size;
    bottom: $corner-size;
    box-sizing: border-box;
    background: v(primaryBackgroundColor);
  }

  .#{$prefix}icon-content {
    height: 100%;

    &.#{$prefix}inline {
      margin-right: $margin-icon-text;
    }
  }

  &.#{$prefix}carrier-button {
    &:focus {
      outline: none;
    }

    &:focus-within {
      outline: none;
    }

    &.#{$prefix}primary,
    &.#{$prefix}secondary {
      .#{$prefix}icon-container,
      .#{$prefix}content-container {
        &:focus {
          outline: none;
        }

        &:focus-within {
          outline: none;
        }
      }
    }
  }

  &.#{$prefix}primary {
    // icon button
    .#{$prefix}icon-container {
      path.#{$prefix}carrier {
        fill: v(primaryBackgroundColor);
      }

      .#{$prefix}icon-content {
        fill: v(primaryTextAndIconColor);
      }

      &:hover {
        path.#{$prefix}carrier {
          fill: v(primaryBackgroundHoverColor);
        }

        .#{$prefix}icon-content {
          fill: v(primaryTextAndIconHoverColor);
        }
      }

      &:focus {
        path.#{$prefix}carrier {
          fill: v(primaryBackgroundFocusColor);
        }
      }

      &.#{$prefix}icon-no-svg {
        width: 20px;
        height: 30px;
        display: flex;
        justify-content: center;
      }
    }

    // text (and icon) button
    .#{$prefix}content-container {
      .#{$prefix}content {
        color: v(primaryTextAndIconColor);

        .#{$prefix}button-border-top,
        .#{$prefix}button-border-bottom {
          stroke: v(primaryBackgroundColor);
        }

        .#{$prefix}contentsvg {
          fill: v(primaryBackgroundColor);
        }

        eon-ui-icon {
          flex-shrink: 0;
        }

        // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
        .#{$prefix}icon-content {
          fill: v(primaryTextAndIconColor);
        }
      }

      .#{$prefix}edge {
        .#{$prefix}contentsvg {
          fill: v(primaryBackgroundColor);
        }

        &.#{$prefix}left {
          border-left: $border-width solid v(primaryBorderColor);
        }

        &.#{$prefix}right {
          border-right: $border-width solid v(primaryBorderColor);
        }
      }

      path.#{$prefix}edge.#{$prefix}solid {
        fill: v(primaryBackgroundColor);
      }

      &:hover {
        .#{$prefix}content {
          color: v(primaryTextAndIconHoverColor);

          .#{$prefix}button-border-top,
          .#{$prefix}button-border-bottom {
            stroke: v(primaryBorderHoverColor);
          }

          .#{$prefix}contentsvg {
            fill: v(primaryBackgroundHoverColor);
          }

          // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
          .#{$prefix}icon-content {
            fill: v(primaryTextAndIconHoverColor);
          }
        }

        .#{$prefix}top {
          border-bottom: 1px solid v(primaryBackgroundHoverColor);
        }

        .#{$prefix}bot {
          border-top: 1px solid v(primaryBackgroundHoverColor);
        }

        .#{$prefix}edge {
          background: v(primaryBackgroundHoverColor);

          .#{$prefix}contentsvg {
            fill: v(primaryBackgroundHoverColor);
          }

          &.#{$prefix}left {
            border-left: $border-width solid v(primaryBorderHoverColor);
          }

          &.#{$prefix}right {
            border-right: $border-width solid v(primaryBorderHoverColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}solid {
          fill: v(primaryBackgroundHoverColor);
        }
      }

      &:focus {
        .#{$prefix}content {
          color: v(primaryBackgroundFocusColor);

          svg {
            stroke: v(primaryBackgroundFocusColor);
          }

          .#{$prefix}contentsvg {
            fill: v(primaryBackgroundFocusColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}solid {
          fill: v(primaryBackgroundFocusColor);
        }
      }

      /* Do not combine with pseudo-classes above!!!
         https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
      &:focus-within {
        .#{$prefix}content {
          color: v(primaryBackgroundFocusColor);
          background-color: v(primaryBackgroundFocusColor);

          svg {
            stroke: v(primaryBackgroundFocusColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}solid {
          fill: v(primaryBackgroundFocusColor);
        }
      }
    }
  }

  &.#{$prefix}secondary {
    // icon button
    .#{$prefix}icon-container {
      &.#{$prefix}icon-no-svg {
        background: v(secondaryBackgroundColor);
        border: #{$button-border-size} solid v(secondaryBorderColor);
        width: 20px;
        height: 30px;
        display: flex;
        justify-content: center;
      }

      path.#{$prefix}carrier {
        fill: v(secondaryBackgroundColor);
        stroke: v(secondaryBorderColor);
      }

      .#{$prefix}icon-content {
        fill: v(secondaryTextAndIconColor);
      }

      &:hover {
        &.#{$prefix}icon-no-svg {
          background: v(secondaryBackgroundHoverColor);
          border-color: v(secondaryBorderHoverColor);
        }

        path.#{$prefix}carrier {
          fill: v(secondaryBackgroundHoverColor);
          stroke: v(secondaryBorderHoverColor);
        }

        .#{$prefix}icon-content {
          fill: v(secondaryTextAndIconHoverColor);
          opacity: 1;
        }
      }

      &:focus {
        path.#{$prefix}carrier {
          fill: v(secondaryBackgroundFocusColor);
        }
      }

      /* Do not combine with pseudo-classes above!!!
         https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
      &:focus-within {
        path.#{$prefix}carrier {
          fill: v(secondaryBackgroundFocusColor);
        }
      }
    }

    // text (and icon) button
    .#{$prefix}content-container {
      .#{$prefix}content {
        color: v(secondaryTextAndIconColor);

        &-no-svg {
          background: v(secondaryBackgroundColor);
          border: #{$button-border-size} solid v(secondaryBorderColor);

          &:hover {
            background: v(secondaryBackgroundHoverColor);
            border-color: v(secondaryBorderHoverColor);
          }
        }

        .#{$prefix}button-border-top,
        .#{$prefix}button-border-bottom {
          stroke: v(secondaryBorderColor);
        }

        .#{$prefix}contentsvg {
          fill: v(secondaryBackgroundColor);
        }

        // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
        .#{$prefix}icon-content {
          fill: v(secondaryTextAndIconColor);
        }
      }

      .#{$prefix}top,
      .#{$prefix}bot {
        border: none;
      }

      .#{$prefix}edge {
        background: v(secondaryBackgroundColor);

        .#{$prefix}contentsvg {
          fill: v(secondaryBackgroundColor);
        }

        &.#{$prefix}left {
          border-left: $border-width solid v(secondaryBorderColor);
        }

        &.#{$prefix}right {
          border-right: $border-width solid v(secondaryBorderColor);
        }
      }

      path.#{$prefix}edge.#{$prefix}outline {
        fill: v(secondaryBackgroundColor);
        stroke: v(secondaryBorderColor);
      }

      &:hover {
        .#{$prefix}content {
          color: v(secondaryTextAndIconHoverColor);

          .#{$prefix}button-border-top,
          .#{$prefix}button-border-bottom {
            stroke: v(secondaryBorderHoverColor);
          }

          .#{$prefix}contentsvg {
            fill: v(secondaryBackgroundHoverColor);
          }

          // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
          .#{$prefix}icon-content {
            fill: v(secondaryTextAndIconHoverColor);
            opacity: 1;
          }

          span {
            opacity: 1;
          }
        }

        .#{$prefix}edge {
          background: v(secondaryBackgroundHoverColor);

          .#{$prefix}contentsvg {
            fill: v(secondaryBackgroundHoverColor);
          }

          &.#{$prefix}left {
            border-left: $border-width solid v(secondaryBackgroundHoverColor);
          }

          &.#{$prefix}right {
            border-right: $border-width solid v(secondaryBackgroundHoverColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}outline {
          fill: v(secondaryBackgroundHoverColor);
          stroke: v(secondaryBorderHoverColor);
        }
      }

      &:focus {
        .#{$prefix}content {
          color: v(secondaryBackgroundFocusColor);
          background-color: v(secondaryBackgroundFocusColor);

          svg {
            stroke: v(secondaryBackgroundFocusColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}outline {
          fill: v(secondaryBackgroundFocusColor);
        }
      }

      /* Do not combine with pseudo-classes above!!!
         https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
      &:focus-within {
        .#{$prefix}content {
          color: v(secondaryBackgroundFocusColor);
          background-color: v(secondaryBackgroundFocusColor);

          svg {
            stroke: v(secondaryBackgroundFocusColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}outline {
          fill: v(secondaryBackgroundFocusColor);
        }
      }
    }

    &.#{$prefix}button-on-image {
      // icon button
      .#{$prefix}icon-container {
        path.#{$prefix}carrier {
          fill: v(secondaryBtnOnImageBackgroundColor);
          stroke: v(secondaryBtnOnImageBackgroundColor);
        }

        .#{$prefix}icon-content {
          fill: v(secondaryBtnOnImageTextAndIconColor);
        }

        &:hover {
          path.#{$prefix}carrier {
            fill: v(secondaryBtnOnImageHoverFocusColor);
            stroke: v(secondaryBtnOnImageHoverFocusColor);
          }

          .#{$prefix}icon-content {
            fill: v(secondaryBtnOnImageTextAndIconHoverColor);
            opacity: 1;
          }
        }

        &:focus {
          path.#{$prefix}carrier {
            fill: v(secondaryBtnOnImageHoverFocusColor);
          }
        }

        /* Do not combine with pseudo-classes above!!!
         https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
        &:focus-within {
          path.#{$prefix}carrier {
            fill: v(secondaryBtnOnImageHoverFocusColor);
          }
        }
      }

      .#{$prefix}content-container {
        .#{$prefix}content {
          color: v(secondaryBtnOnImageTextAndIconColor);
          background-color: v(secondaryBtnOnImageBackgroundColor);

          .#{$prefix}button-border-top,
          .#{$prefix}button-border-bottom {
            stroke: v(secondaryBtnOnImageBackgroundColor);
          }

          // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
          .#{$prefix}icon-content {
            fill: v(secondaryBtnOnImageTextAndIconColor);
          }
        }

        .#{$prefix}edge {
          .#{$prefix}contentsvg {
            fill: v(secondaryBtnOnImageBackgroundColor);
          }

          &.#{$prefix}left {
            border-left: $border-width solid v(secondaryBtnOnImageBackgroundColor);
          }

          &.#{$prefix}right {
            border-right: $border-width solid v(secondaryBtnOnImageBackgroundColor);
          }
        }

        path.#{$prefix}edge.#{$prefix}outline {
          fill: v(secondaryBtnOnImageBackgroundColor);
          stroke: v(secondaryBtnOnImageBackgroundColor);
        }

        path.#{$prefix}outline {
          stroke-width: $border-width;
        }

        &:hover {
          .#{$prefix}content {
            color: v(secondaryBtnOnImageTextAndIconHoverColor);

            .#{$prefix}button-border-top,
            .#{$prefix}button-border-bottom {
              stroke: v(secondaryBtnOnImageHoverFocusColor);
            }

            .#{$prefix}contentsvg {
              fill: v(secondaryBtnOnImageHoverFocusColor);
            }

            // nested class selector is required because it has a higher specificity than the eon-ui-icon internal default scheme
            .#{$prefix}icon-content {
              fill: v(secondaryBtnOnImageTextAndIconHoverColor);
              opacity: 1;
            }

            span {
              opacity: 1;
            }
          }

          .#{$prefix}edge {
            .#{$prefix}contentsvg {
              fill: v(secondaryBtnOnImageBackgroundColor);
            }

            &.#{$prefix}left {
              border-left: $border-width solid v(secondaryBtnOnImageHoverFocusColor);
            }

            &.#{$prefix}right {
              border-right: $border-width solid v(secondaryBtnOnImageHoverFocusColor);
            }
          }

          path.#{$prefix}edge.#{$prefix}outline {
            fill: v(secondaryBtnOnImageHoverFocusColor);
            stroke: v(secondaryBtnOnImageHoverFocusColor);
          }

          path.#{$prefix}outline {
            stroke-width: $border-width;
          }
        }

        &:focus {
          .#{$prefix}content {
            color: v(secondaryBtnOnImageHoverFocusColor);
            background-color: v(secondaryBtnOnImageHoverFocusColor);

            svg {
              stroke: v(secondaryBtnOnImageHoverFocusColor);
            }
          }

          path.#{$prefix}edge.#{$prefix}outline {
            fill: v(secondaryBtnOnImageHoverFocusColor);
          }
        }

        /* Do not combine with pseudo-classes above!!!
           https://medium.#{$prefix}com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f */
        &:focus-within {
          .#{$prefix}content {
            color: v(secondaryBtnOnImageHoverFocusColor);
            background-color: v(secondaryBtnOnImageHoverFocusColor);

            svg {
              stroke: v(secondaryBtnOnImageHoverFocusColor);
            }
          }

          path.#{$prefix}edge.#{$prefix}outline {
            fill: v(secondaryBtnOnImageHoverFocusColor);
          }
        }
      }

      &:focus {
        @include focusBorder(v(secondaryBtnOnImageFocusBorderColor));
      }

      &:focus-within {
        @include focusBorder(v(secondaryBtnOnImageFocusBorderColor));
      }
    }
  }

  /** Small button **/
  &.#{$prefix}small {
    height: auto;
    min-height: $height-small;
    font-size: 16px;
    line-height: $line-height-small;

    span {
      font-weight: 500;
    }

    .#{$prefix}content {
      min-height: $heightWithoutBorder-small;
      margin: 0px 15px 0px 15px;

      &.#{$prefix}content-no-svg {
        margin: 0;
      }

      /* Dirty MS Edge 18 fix */
      @supports (-ms-ime-align: auto) {
        min-height: $height-small;
      }
    }

    .#{$prefix}corner,
    .#{$prefix}edge {
      width: $corner-size-small;
    }

    .#{$prefix}edge {
      top: $corner-size-small;
      bottom: $corner-size-small;
    }

    .#{$prefix}icon-content {
      height: $line-height-small;
    }

    &.#{$prefix}primary,
    &.#{$prefix}secondary {
      // icon button
      .#{$prefix}icon-container {
        &.#{$prefix}icon-no-svg {
          width: 4px;
          height: 14px;
          display: flex;
          justify-content: center;
        }
      }
    }
  }

  @include for-size(phone) {
    &.#{$prefix}full-width-button {
      width: 100%;

      .#{$prefix}content-container {
        display: block;
        width: 100%;

        .#{$prefix}content {
          justify-content: center;
        }
      }

      /** Small button **/
      &.#{$prefix}small {
        .#{$prefix}svg-edge-left,
        .#{$prefix}svg-edge-right {
          height: $height-small;
        }
      }
    }
  }

  .#{$prefix}button-border-top,
  .#{$prefix}button-border-bottom {
    position: absolute;
    left: 0;
    stroke-width: 4;
  }

  .#{$prefix}button-border-top {
    top: 0;
  }

  .#{$prefix}button-border-bottom {
    bottom: 0;
  }

  eon-ui-preloader-squares {
    z-index: 1;
  }
}

a {
  display: table;

  &:focus {
    outline: none;
  }

  &:focus-within {
    outline: none;
  }
}
